import React from "react";
import { Link, useNavigate } from "react-router-dom";
import clsx from "clsx";
import logoLg from "../../assets/images/lg-logo.png";
import { ONBOARDING_STEPS, getStepPath } from "./onboardingSteps";

const CheckIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M3.5 8.5l3 3 6-7"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const OnboardingSetupLayout = ({
  currentStep,
  title,
  description,
  children,
}) => {
  const navigate = useNavigate();
  const year = new Date().getFullYear();
  const current = ONBOARDING_STEPS.find((step) => step.id === currentStep);
  const progress = (currentStep / ONBOARDING_STEPS.length) * 100;

  const goToStep = (stepId) => {
    if (stepId >= currentStep) {
      return;
    }
    navigate(getStepPath(stepId));
  };

  return (
    <div className="onboarding-setup onboarding-setup--page">
      <aside className="onboarding-setup__aside">
        <div className="onboarding-setup__atmosphere" aria-hidden="true">
          <span className="onboarding-setup__orb onboarding-setup__orb--a" />
          <span className="onboarding-setup__orb onboarding-setup__orb--b" />
        </div>
        <div className="onboarding-setup__aside-inner">
          <div className="onboarding-setup__brand">
            <img className="onboarding-setup__logo" src={logoLg} alt="MeetOcto" />
            <span className="onboarding-setup__badge">Workspace setup</span>
          </div>

          <ol className="onboarding-setup__rail" aria-label="Setup steps">
            {ONBOARDING_STEPS.map((step, index) => {
              const state =
                step.id < currentStep
                  ? "done"
                  : step.id === currentStep
                  ? "current"
                  : "upcoming";
              const clickable = step.id < currentStep;
              return (
                <li
                  key={step.id}
                  className={clsx(
                    "onboarding-setup__rail-item",
                    `is-${state}`
                  )}
                >
                  {index < ONBOARDING_STEPS.length - 1 ? (
                    <span className="onboarding-setup__rail-line" aria-hidden="true" />
                  ) : null}
                  <button
                    type="button"
                    className="onboarding-setup__rail-btn"
                    onClick={() => goToStep(step.id)}
                    disabled={!clickable}
                    aria-current={state === "current" ? "step" : undefined}
                  >
                    <span className="onboarding-setup__rail-index">
                      {state === "done" ? <CheckIcon /> : step.id}
                    </span>
                    <span className="onboarding-setup__rail-copy">
                      <strong>{step.label}</strong>
                      <span>{step.hint}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>

          <div className="onboarding-setup__aside-foot">
            <div className="onboarding-setup__progress">
              <span>
                Step {currentStep} of {ONBOARDING_STEPS.length}
              </span>
              <div
                className="onboarding-setup__progress-track"
                role="progressbar"
                aria-valuemin={1}
                aria-valuemax={ONBOARDING_STEPS.length}
                aria-valuenow={currentStep}
              >
                <span style={{ width: `${progress}%` }} />
              </div>
            </div>
            <p>© {year} MeetOcto. A JB Media LLC company. All Rights Reserved.</p>
          </div>
        </div>
      </aside>

      <main className="onboarding-setup__main">
        <div className="onboarding-setup__mobile-bar">
          <img src={logoLg} alt="MeetOcto" />
          <ol className="onboarding-setup__dots" aria-label="Setup progress">
            {ONBOARDING_STEPS.map((step) => (
              <li
                key={step.id}
                className={clsx({
                  "is-done": step.id < currentStep,
                  "is-current": step.id === currentStep,
                })}
              >
                <span className="visually-hidden">
                  {step.label}
                  {step.id === currentStep ? " (current)" : ""}
                </span>
              </li>
            ))}
          </ol>
        </div>

        <div className="onboarding-setup__card">
          <header className="onboarding-setup__header">
            <p className="onboarding-setup__eyebrow">
              Step {currentStep} of {ONBOARDING_STEPS.length}
              {current ? ` · ${current.label}` : ""}
            </p>
            <h1>{title}</h1>
            {description ? (
              <p className="onboarding-setup__lead">{description}</p>
            ) : null}
          </header>
          {children}
        </div>
      </main>
    </div>
  );
};

export const OnboardingFieldError = ({ children, ...rest }) => {
  if (!children) {
    return null;
  }
  return (
    <span className="onboarding-setup__error" role="alert" {...rest}>
      {children}
    </span>
  );
};

export const OnboardingActions = ({
  onBack,
  skipTo = "/signup/payment",
  skipLabel = "I’ll do this later",
  showSkip = true,
  submitLabel = "Continue",
}) => (
  <div className="onboarding-setup__actions">
    {onBack ? (
      <button
        type="button"
        className="onboarding-setup__btn onboarding-setup__btn--ghost"
        onClick={onBack}
      >
        Back
      </button>
    ) : (
      <span className="onboarding-setup__actions-spacer" />
    )}
    <div className="onboarding-setup__actions-end">
      {showSkip ? (
        <Link to={skipTo} className="onboarding-setup__skip">
          {skipLabel}
        </Link>
      ) : null}
      <button
        type="submit"
        className="onboarding-setup__btn onboarding-setup__btn--primary"
      >
        {submitLabel}
      </button>
    </div>
  </div>
);

export default OnboardingSetupLayout;
